/**
 * Familienkalender – was Oberfläche und Erinnerungsmail gemeinsam brauchen.
 *
 * Die historischen Ereignisse stehen hier und nicht in der Seite, weil die
 * Erinnerungsmail sie ebenfalls verschickt. Zwei Listen liefen auseinander:
 * die Seite hätte den Mauerfall, die Mail nicht.
 */

export const KALENDER_ARTEN = [
  'birthday',
  'death_anniversary',
  'marriage_anniversary',
  'historical',
] as const;

export type KalenderArt = (typeof KALENDER_ARTEN)[number];

export const KALENDER_ART_LABELS: Record<KalenderArt, string> = {
  birthday: 'Geburtstage',
  death_anniversary: 'Todestage',
  marriage_anniversary: 'Hochzeitstage',
  historical: 'Zeitgeschichte',
};

export function istKalenderArt(wert: unknown): wert is KalenderArt {
  return typeof wert === 'string' && (KALENDER_ARTEN as readonly string[]).includes(wert);
}

export const KALENDER_RHYTHMEN = ['taeglich', 'woechentlich'] as const;
export type KalenderRhythmus = (typeof KALENDER_RHYTHMEN)[number];

// ─── Historische Ereignisse ──────────────────────────────────────────────────
//
// Ausgewählt nach dem, was in Familiengeschichten Spuren hinterlässt: Kriege
// und Währungen, aber auch das, was für die Forschung selbst zählt – ab wann
// es Kirchenbücher gibt, wann Standesämter kamen, wann der Kalender sprang.
// Deshalb steht bei vielen ein Satz dazu, warum das für Ahnenforscher wichtig
// ist.

export type HistorischeKategorie = 'krieg' | 'politik' | 'gesellschaft' | 'forschung';

export const HISTORISCHE_KATEGORIE_LABELS: Record<HistorischeKategorie, string> = {
  krieg: 'Krieg & Frieden',
  politik: 'Politik',
  gesellschaft: 'Gesellschaft',
  forschung: 'Für die Forschung',
};

export interface HistorischesEreignis {
  month: number;
  day: number;
  year: number;
  titel: string;
  /** Ein Satz Einordnung – bevorzugt, was es für Familien bedeutete. */
  hinweis?: string;
  /** Bestimmt das Icon in der Oberfläche – keine Emojis in den Daten. */
  kategorie: HistorischeKategorie;
}

export const HISTORISCHE_EREIGNISSE: readonly HistorischesEreignis[] = [
  { year: 800, month: 12, day: 25, kategorie: 'politik', titel: 'Kaiserkrönung Karls des Großen' },
  { year: 1492, month: 10, day: 12, kategorie: 'gesellschaft', titel: 'Kolumbus erreicht Amerika' },
  { year: 1517, month: 10, day: 31, kategorie: 'gesellschaft', titel: 'Luthers 95 Thesen – Beginn der Reformation',
    hinweis: 'Ab hier trennen sich evangelische und katholische Kirchenbücher.' },
  { year: 1563, month: 11, day: 11, kategorie: 'forschung', titel: 'Konzil von Trient: Dekret „Tametsi"',
    hinweis: 'Verpflichtet katholische Pfarreien zum Führen von Trau- und Taufbüchern.' },
  { year: 1582, month: 10, day: 15, kategorie: 'forschung', titel: 'Gregorianischer Kalender eingeführt',
    hinweis: 'Auf den 4. folgte der 15. Oktober. Ältere Daten können zehn Tage abweichen.' },
  { year: 1618, month: 5, day: 23, kategorie: 'krieg', titel: 'Prager Fenstersturz – Beginn des Dreißigjährigen Kriegs',
    hinweis: 'Viele Kirchenbücher sind in diesen Jahren verbrannt oder brechen ab.' },
  { year: 1648, month: 10, day: 24, kategorie: 'krieg', titel: 'Westfälischer Friede' },
  { year: 1700, month: 3, day: 1, kategorie: 'forschung', titel: 'Evangelische Länder übernehmen den neuen Kalender',
    hinweis: 'Auf den 18. Februar folgte der 1. März 1700.' },
  { year: 1755, month: 11, day: 1, kategorie: 'gesellschaft', titel: 'Erdbeben von Lissabon' },
  { year: 1776, month: 7, day: 4, kategorie: 'politik', titel: 'Unabhängigkeitserklärung der USA' },
  { year: 1789, month: 7, day: 14, kategorie: 'politik', titel: 'Sturm auf die Bastille' },
  { year: 1805, month: 12, day: 2, kategorie: 'krieg', titel: 'Schlacht bei Austerlitz' },
  { year: 1806, month: 8, day: 6, kategorie: 'politik', titel: 'Ende des Heiligen Römischen Reiches' },
  { year: 1806, month: 10, day: 14, kategorie: 'krieg', titel: 'Schlacht bei Jena und Auerstedt' },
  { year: 1807, month: 10, day: 9, kategorie: 'gesellschaft', titel: 'Oktoberedikt: Bauernbefreiung in Preußen',
    hinweis: 'Ab jetzt durften Bauern frei heiraten und wegziehen.' },
  { year: 1812, month: 3, day: 11, kategorie: 'forschung', titel: 'Preußisches Judenedikt',
    hinweis: 'Jüdische Familien mussten feste Familiennamen annehmen.' },
  { year: 1813, month: 10, day: 16, kategorie: 'krieg', titel: 'Beginn der Völkerschlacht bei Leipzig' },
  { year: 1815, month: 6, day: 9, kategorie: 'politik', titel: 'Schlussakte des Wiener Kongresses' },
  { year: 1815, month: 6, day: 18, kategorie: 'krieg', titel: 'Schlacht bei Waterloo' },
  { year: 1834, month: 1, day: 1, kategorie: 'politik', titel: 'Deutscher Zollverein tritt in Kraft' },
  { year: 1835, month: 12, day: 7, kategorie: 'gesellschaft', titel: 'Erste deutsche Eisenbahn (Nürnberg–Fürth)' },
  { year: 1848, month: 3, day: 18, kategorie: 'politik', titel: 'Märzrevolution: Barrikadenkämpfe in Berlin',
    hinweis: 'Nach dem Scheitern wanderten viele „Forty-Eighters" nach Amerika aus.' },
  { year: 1848, month: 5, day: 18, kategorie: 'politik', titel: 'Nationalversammlung in der Paulskirche' },
  { year: 1871, month: 1, day: 18, kategorie: 'politik', titel: 'Gründung des Deutschen Kaiserreichs' },
  { year: 1874, month: 10, day: 1, kategorie: 'forschung', titel: 'Standesämter in Preußen',
    hinweis: 'Geburten, Ehen und Sterbefälle werden ab jetzt staatlich beurkundet.' },
  { year: 1876, month: 1, day: 1, kategorie: 'forschung', titel: 'Standesämter im ganzen Deutschen Reich',
    hinweis: 'Ab hier gibt es neben dem Kirchenbuch immer auch eine Urkunde.' },
  { year: 1892, month: 1, day: 1, kategorie: 'forschung', titel: 'Ellis Island öffnet',
    hinweis: 'Die Passagierlisten der Einwanderer nach New York sind erhalten.' },
  { year: 1900, month: 1, day: 1, kategorie: 'gesellschaft', titel: 'Bürgerliches Gesetzbuch tritt in Kraft' },
  { year: 1912, month: 4, day: 15, kategorie: 'gesellschaft', titel: 'Untergang der Titanic' },
  { year: 1914, month: 6, day: 28, kategorie: 'krieg', titel: 'Attentat von Sarajevo' },
  { year: 1914, month: 7, day: 28, kategorie: 'krieg', titel: 'Beginn des Ersten Weltkriegs',
    hinweis: 'Verlustlisten und Kriegsstammrollen sind wichtige Quellen.' },
  { year: 1918, month: 11, day: 9, kategorie: 'politik', titel: 'Ausrufung der Republik' },
  { year: 1918, month: 11, day: 11, kategorie: 'krieg', titel: 'Waffenstillstand – Ende des Ersten Weltkriegs' },
  { year: 1919, month: 8, day: 11, kategorie: 'politik', titel: 'Weimarer Verfassung unterzeichnet' },
  { year: 1923, month: 11, day: 15, kategorie: 'gesellschaft', titel: 'Rentenmark beendet die Hyperinflation' },
  { year: 1929, month: 10, day: 24, kategorie: 'gesellschaft', titel: 'Börsenkrach in New York – Weltwirtschaftskrise' },
  { year: 1933, month: 1, day: 30, kategorie: 'politik', titel: 'Hitler wird Reichskanzler' },
  { year: 1938, month: 11, day: 9, kategorie: 'politik', titel: 'Novemberpogrome' },
  { year: 1939, month: 9, day: 1, kategorie: 'krieg', titel: 'Überfall auf Polen – Beginn des Zweiten Weltkriegs' },
  { year: 1941, month: 6, day: 22, kategorie: 'krieg', titel: 'Überfall auf die Sowjetunion' },
  { year: 1944, month: 6, day: 6, kategorie: 'krieg', titel: 'Landung der Alliierten in der Normandie' },
  { year: 1944, month: 7, day: 20, kategorie: 'politik', titel: 'Attentat auf Hitler (Stauffenberg)' },
  { year: 1945, month: 1, day: 27, kategorie: 'politik', titel: 'Befreiung von Auschwitz' },
  { year: 1945, month: 2, day: 13, kategorie: 'krieg', titel: 'Luftangriffe auf Dresden' },
  { year: 1945, month: 5, day: 8, kategorie: 'krieg', titel: 'Kriegsende in Europa',
    hinweis: 'Millionen Vertriebene – Suchdienst und Heimatortskarteien helfen bei der Spurensuche.' },
  { year: 1945, month: 8, day: 2, kategorie: 'politik', titel: 'Potsdamer Abkommen' },
  { year: 1948, month: 6, day: 20, kategorie: 'gesellschaft', titel: 'Währungsreform: Einführung der D-Mark' },
  { year: 1948, month: 6, day: 24, kategorie: 'politik', titel: 'Beginn der Berlin-Blockade' },
  { year: 1948, month: 12, day: 10, kategorie: 'politik', titel: 'Allgemeine Erklärung der Menschenrechte' },
  { year: 1949, month: 5, day: 23, kategorie: 'politik', titel: 'Grundgesetz verkündet' },
  { year: 1949, month: 10, day: 7, kategorie: 'politik', titel: 'Gründung der DDR' },
  { year: 1953, month: 6, day: 17, kategorie: 'politik', titel: 'Volksaufstand in der DDR' },
  { year: 1958, month: 1, day: 1, kategorie: 'politik', titel: 'Europäische Wirtschaftsgemeinschaft' },
  { year: 1961, month: 8, day: 13, kategorie: 'politik', titel: 'Bau der Berliner Mauer' },
  { year: 1961, month: 10, day: 30, kategorie: 'gesellschaft', titel: 'Anwerbeabkommen mit der Türkei' },
  { year: 1969, month: 7, day: 20, kategorie: 'gesellschaft', titel: 'Mondlandung' },
  { year: 1986, month: 4, day: 26, kategorie: 'gesellschaft', titel: 'Reaktorunglück von Tschernobyl' },
  { year: 1989, month: 11, day: 9, kategorie: 'politik', titel: 'Fall der Berliner Mauer' },
  { year: 1990, month: 10, day: 3, kategorie: 'politik', titel: 'Deutsche Einheit' },
  { year: 2001, month: 9, day: 11, kategorie: 'politik', titel: 'Anschläge vom 11. September' },
  { year: 2002, month: 1, day: 1, kategorie: 'gesellschaft', titel: 'Euro-Bargeld eingeführt' },
];

// ─── Jahrestage ──────────────────────────────────────────────────────────────

/** 10, 20, 25, 30 … 50, 75, 100, 125, 150 … – das, was man feiert. */
export function istRunderJahrestag(jahre: number): boolean {
  return jahre > 0 && (jahre % 10 === 0 || jahre % 25 === 0);
}

const HOCHZEITS_NAMEN: Record<number, string> = {
  25: 'Silberhochzeit',
  30: 'Perlenhochzeit',
  40: 'Rubinhochzeit',
  50: 'Goldene Hochzeit',
  60: 'Diamantene Hochzeit',
  65: 'Eiserne Hochzeit',
  70: 'Gnadenhochzeit',
  75: 'Kronjuwelenhochzeit',
};

export function hochzeitsName(jahre: number): string | null {
  return HOCHZEITS_NAMEN[jahre] ?? null;
}

/**
 * Was der Jahrestag ist, in Worten: „wird 80", „100. Todestag",
 * „Goldene Hochzeit". `lebt` gilt nur für Geburtstage.
 */
export function jahrestagText(art: KalenderArt, jahre: number, lebt = false): string {
  if (jahre <= 0) {
    if (art === 'birthday') return 'Geburt';
    if (art === 'death_anniversary') return 'Todestag';
    if (art === 'marriage_anniversary') return 'Hochzeit';
    return 'Ereignis';
  }
  switch (art) {
    case 'birthday':
      return lebt ? `wird ${jahre}` : `${jahre}. Geburtstag`;
    case 'death_anniversary':
      return `${jahre}. Todestag`;
    case 'marriage_anniversary': {
      const name = hochzeitsName(jahre);
      return name ? `${name} (${jahre} Jahre)` : `${jahre}. Hochzeitstag`;
    }
    case 'historical':
      return `vor ${jahre} Jahren`;
  }
}

// ─── Datumsrechnung ohne Zeitzonen ───────────────────────────────────────────

export interface Kalendertag {
  year: number;
  month: number;
  day: number;
}

export function istSchaltjahr(jahr: number): boolean {
  return (jahr % 4 === 0 && jahr % 100 !== 0) || jahr % 400 === 0;
}

/**
 * Fällt ein Ereignis vom (Monat, Tag) in diesem Jahr auf `tag`?
 * Der 29. Februar wird in Nicht-Schaltjahren am 28. gefeiert – sonst ginge
 * er in drei von vier Jahren verloren.
 */
export function faelltAuf(month: number, day: number, tag: Kalendertag): boolean {
  if (month === 2 && day === 29 && !istSchaltjahr(tag.year)) {
    return tag.month === 2 && tag.day === 28;
  }
  return month === tag.month && day === tag.day;
}

/** `tag` plus n Tage, über Monats- und Jahresgrenzen hinweg. */
export function plusTage(tag: Kalendertag, n: number): Kalendertag {
  const d = new Date(Date.UTC(tag.year, tag.month - 1, tag.day + n));
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
}

export function kalendertagSchluessel(tag: Kalendertag): string {
  return `${tag.year}-${String(tag.month).padStart(2, '0')}-${String(tag.day).padStart(2, '0')}`;
}
